import type { SVGProps, ReactNode } from "react";
import { LayersIcon, CompassIcon } from "@/components/feature-icons";
import {
  HubIcon,
  CodeIcon,
  IntegrationIcon,
  PropertyIcon,
  DomainIcon,
  ShieldCheckIcon,
  CalendarCheckIcon,
  ContractIcon,
} from "@/components/icons";
import { RouteIcon, SlidersIcon } from "@/app/pricing/pricing-icons";

type IconProps = SVGProps<SVGSVGElement>;

/** Local inline icons, matched to the project's 24px / 1.75 stroke line style. */
function Svg({ children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function UsersIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
      <circle cx="9" cy="7" r="3.5" />
      <path d="M22 19v-1a4 4 0 0 0-3-3.87M16 3.5a4 4 0 0 1 0 7" />
    </Svg>
  );
}

export function ActivityIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 12h4l3 8 4-16 3 8h4" />
    </Svg>
  );
}

export function BriefcaseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 12h18" />
    </Svg>
  );
}

export function HeadphonesIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
      <path d="M4 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2ZM20 14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2Z" />
    </Svg>
  );
}

export function UnlockIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="10" width="16" height="11" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 7.6-1.8" />
    </Svg>
  );
}

export function WorkflowIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <path d="M10 6.5h4a3 3 0 0 1 3 3V14" />
    </Svg>
  );
}

/** Header glyph: two comparison columns with a divider (not scales-of-justice). */
export function CompareGlyphIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 4v16" />
      <path d="M4 8h5M4 12h5M4 16h5" />
      <path d="M15 8h5M15 12h5M15 16h5" />
    </Svg>
  );
}

export function CalculatorIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 7h8M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01M16 16h.01" />
    </Svg>
  );
}

export function FileTextIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5M9 13h6M9 17h6" />
    </Svg>
  );
}

export function FileChartIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5M9 17v-3M12.5 17v-5M16 17v-2" />
    </Svg>
  );
}

/* ----------------------------------------------- keyed glyph lookup */

const MAP: Record<string, (p: IconProps) => ReactNode> = {
  platform: LayersIcon,
  intelligence: HubIcon,
  api: CodeIcon,
  integration: IntegrationIcon,
  consultancy: CompassIcon,
  implementation: SlidersIcon,
  casework: RouteIcon,
  licence: UsersIcon,
  transactions: ActivityIcon,
  services: BriefcaseIcon,
  house: PropertyIcon,
  building: DomainIcon,
  shield: ShieldCheckIcon,
  support: HeadphonesIcon,
  calendar: CalendarCheckIcon,
  unlock: UnlockIcon,
  workflow: WorkflowIcon,
  code: CodeIcon,
  users: UsersIcon,
  activity: ActivityIcon,
  briefcase: BriefcaseIcon,
  plug: IntegrationIcon,
  book: ContractIcon,
  layers: LayersIcon,
  document: FileTextIcon,
  filechart: FileChartIcon,
};

export function Glyph({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  const Cmp = MAP[name];
  if (!Cmp) return null;
  return <Cmp className={className} aria-hidden="true" />;
}
